import { useEffect, useRef, useState } from 'react';
import { Columns3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_COLUMN_CONFIG,
  ORDER_TRACKING_OPTIONAL_COLUMNS,
  type OrderTrackingColumnId,
  persistOrderTrackingColumnConfig,
} from '@/modules/orderTracking/config/orderTrackingTableConfig';

interface OrderTrackingColumnPickerProps {
  columnConfig: Record<string, boolean>;
  onChange: (config: Record<string, boolean>) => void;
  hiddenColumnIds?: OrderTrackingColumnId[];
}

export function OrderTrackingColumnPicker({
  columnConfig,
  onChange,
  hiddenColumnIds = [],
}: OrderTrackingColumnPickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const visibleOptions = ORDER_TRACKING_OPTIONAL_COLUMNS.filter(
    (option) => !hiddenColumnIds.includes(option.id)
  );

  const handleToggle = (id: OrderTrackingColumnId, checked: boolean) => {
    const next = { ...columnConfig, [id]: checked };
    onChange(next);
    persistOrderTrackingColumnConfig(next);
  };

  const handleReset = () => {
    const next = { ...DEFAULT_COLUMN_CONFIG };
    onChange(next);
    persistOrderTrackingColumnConfig(next);
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50"
      >
        <Columns3 className="h-4 w-4" />
        {t('orderTracking.columnPicker.title')}
      </button>

      {open && (
        <div className="absolute right-0 bottom-full z-50 mb-2 w-72 rounded-lg border border-gray-200 bg-white shadow-lg p-4">
          <p className="text-sm font-semibold text-gray-900 mb-1">{t('orderTracking.columnPicker.title')}</p>
          <p className="text-xs text-gray-500 mb-3">{t('orderTracking.columnPicker.description')}</p>
          <div className="max-h-64 overflow-y-auto space-y-2">
            {visibleOptions.map(({ id, labelKey }) => (
              <label key={id} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={columnConfig[id] !== false}
                  onChange={(e) => handleToggle(id, e.target.checked)}
                  className="h-4 w-4 text-[#1681ba] border-gray-300 rounded focus:ring-[#1681ba]"
                />
                {t(labelKey)}
              </label>
            ))}
          </div>
          <button
            type="button"
            onClick={handleReset}
            className="mt-3 text-xs font-medium text-[#1681ba] hover:text-[#1370a3]"
          >
            {t('orderTracking.columnPicker.reset')}
          </button>
        </div>
      )}
    </div>
  );
}
